import { UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { ParametersType } from 'data/generated/graphql';
import { useAppQuery } from 'data/query-client';
import { APIErrorResponse } from 'data/query-client/useAppQuery';
import restClient from 'data/restClient';
import { useAuthToken } from 'hooks/useAuthToken';
import { AllessehContentItemAttributes } from './useAllessehContentQuery';

export type AllessehSummariesIncludedAttributes = {
  data: {
    id: string;
    type: 'SummaryModel';
    attributes: AllessehContentItemAttributes;
  };
};

type AllessehSummariesIncludedProperty = {
  id: string;
  type: string;
  attributes: AllessehSummariesIncludedAttributes;
  meta: {
    ref_id: string;
  };
};

export type CollectionNode = {
  id?: string;
  collection?: CollectionNode[];
  updated_datetime_utc?: string;
  publish_version?: string;
  parameters?: ParametersType[];
};

/**
 * Represents the response of the Allesseh summaries endpoint
 */
export type AllessehSummariesResponse = {
  id: string;
  jsonapi: {
    version: '1.0';
  };
  errors: any[];
  data: {
    id: string;
    attributes: {
      status?: string;
      type?: string;
      collection: Record<string, unknown>[];
      updated_datetime_utc?: string;
      publish_version?: string;
      id?: string;
      parameters?: ParametersType[];
    };
  };
  included: AllessehSummariesIncludedProperty[]; // data of articles in collection
  meta: {
    article_count: number;
    collection_count: number;
    query_count: number;
    name?: string;
    is_archived?: boolean;
    publication_key?: string;
    notes?: string;
    creator_user?: string;
    revisor_user?: string;
    origin?: string;
    consumer?: string;
    created_utc?: string;
    collection_type?: string;
  };
};

export type BasicAllessehCollection = {
  base_doc_id: string;
  id: string;
  parameters: ParametersType[];
  status?: string;
  type?: string;
};

export type AllessehResponse = {
  id: string;
  jsonapi: {
    version: '1.0';
  };
  data: {
    id: string;
    attributes: {
      status: string;
      type: string;
      collection: CollectionNode[];
      updated_datetime_utc?: string;
      publish_utc?: string;
      publish_version?: string;
      id?: string;
      parameters?: ParametersType[];
    };
  };
  meta: {
    article_count: number;
    collection_count: number;
    query_count: number;
    name?: string;
    is_archived?: boolean;
    publication_key?: string;
    notes?: string;
    creator_user?: string;
    revisor_user?: string;
    origin?: string;
    consumer?: string;
    created_utc?: string;
    collection_type?: string;
  };
};

const getAllessehCollectionSummariesQuery = (authToken: string, allessehCollectionId: string, count?: number) =>
  restClient.get<AllessehSummariesResponse>('allesseh/collectionSummaries', {
    headers: {
      Authorization: authToken
    },
    params: {
      id: allessehCollectionId,
      ...(count && { count })
    }
  });

type AllessehCollectionSummariesQueryOptions = UseQueryOptions<
  AllessehSummariesResponse,
  AxiosError<APIErrorResponse>,
  AllessehSummariesResponse,
  string[]
> & { shouldShowErrorAlert?: boolean; count?: number };

export const useAllessehCollectionSummariesQuery = (
  allessehCollectionId: string,
  options?: AllessehCollectionSummariesQueryOptions
) => {
  const authToken = useAuthToken();
  const { count, ...queryOptions } = options ?? {};

  return useAppQuery(
    ['useAllessehCollectionSummariesQuery', allessehCollectionId, String(count ?? '')],
    () => getAllessehCollectionSummariesQuery(authToken, allessehCollectionId, count),
    queryOptions
  );
};
